//
//  mandate.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Birth-wizard intake: the ten questions (a hand-kept copy of the intake schema
//  from the TRXY-Signet repo) and the client-side authority ceiling table.
//  POST /v1/mandates/intake/check is the ceiling of record; the local table only
//  pre-checks instantly and must be kept in agreement with the server.
//
// Mirrors ../../../mandates/intake-schema.json (a different repo, `TRXY-Signet`; read-only, not
// bundled). `ui` never edits that file -- this is a hand-kept copy of its ten questions so the
// birth wizard can render them without a cross-repo import. Keep in sync by inspection when the
// schema changes; the source of truth is the schema file, not this one.
import { api } from './client'
import { isOk } from './envelope'
import type { IntakeCeilingDetails } from './types'
import type { TenantRole } from '../auth'

export type IntakeQuestion = {
  id: string
  prompt: string
  mapsTo: string
  type: 'string' | 'multi' | 'rules' | 'object' | 'enum'
  options?: string[]
}

export const INTAKE_QUESTIONS: IntakeQuestion[] = [
  { id: 'agent', prompt: 'Agent name (notebook id)', mapsTo: 'agent', type: 'string' },
  { id: 'role', prompt: 'One-line role: what is this agent for?', mapsTo: 'notes', type: 'string' },
  { id: 'scopes', prompt: 'What may it touch? (scopes)', mapsTo: 'scopes', type: 'multi', options: ['read.source', 'write.repo', 'read.data', 'write.data', 'call.api', 'deploy'] },
  { id: 'never', prompt: 'What must it NEVER do? (hard prohibitions)', mapsTo: 'forbid_bash', type: 'rules' },
  { id: 'escalate', prompt: 'What must it escalate to a human?', mapsTo: 'protect_write', type: 'rules' },
  { id: 'budget', prompt: 'Daily budget (USD) and token ceiling', mapsTo: 'budget', type: 'object' },
  { id: 'environments', prompt: 'Which environments may it run in?', mapsTo: 'environments', type: 'multi', options: ['local-dev', 'cloud-dev', 'prod'] },
  { id: 'model', prompt: 'Model tier', mapsTo: 'cost_policy.model', type: 'enum', options: ['claude-haiku-4-5-20251001', 'claude-sonnet-5', 'claude-opus-5'] },
  { id: 'effort', prompt: 'Reasoning effort', mapsTo: 'cost_policy.effort', type: 'enum', options: ['low', 'medium', 'high'] },
  { id: 'contracts', prompt: 'Cross-service boundaries and their source-of-truth contracts', mapsTo: 'contracts', type: 'rules' },
]

/**
 * A client-side copy of the ceiling table for an INSTANT pre-check while `POST
 * /v1/mandates/intake/check` is in flight. `POST /v1/mandates/intake/check` (spec §6b) is the
 * ceiling of record -- these numbers are pending Bob's confirmation (per team-lead 2026-09-22) and
 * must always be driven back into agreement with the server table, never the other way round.
 * `checkIntakeAnswer` below is what actually decides a refusal; `overreach` only avoids a round
 * trip for the common case and must never be trusted alone.
 */
export type AuthorityCeiling = {
  scopes: string[]
  environments: string[]
  models: string[]
  maxDailyBudgetUsd: number
}

// Mirrors `api/services/mandates.py::CEILINGS` (the server's copy of record) verbatim -- keyed
// directly by the tenant role vocabulary (27 §4/§5, spec §6b; lead ruling 2026-09-23). Numbers are
// unchanged from the pre-RB-13 three-tier table, only the keys moved: `owner` <- old `superadmin`,
// `security-admin`/`team-admin` <- old `admin` (both get the same ceiling until they're told to
// differ), `member` <- old `user`. `auditor` and every platform role carry no entry at all --
// `auditor` only ever previews (27 §4), and a mint/author/patch route's own `require_role` already
// refuses it before this is reached; `ceilingFor` falls back to `member`'s row rather than
// throwing, matching the server's `CEILINGS.get(role, CEILINGS["member"])`, since `POST
// /v1/mandates/intake/check` itself is reachable by any tenant role including `auditor`.
const CEILINGS: Partial<Record<TenantRole, AuthorityCeiling>> = {
  member: {
    scopes: ['read.source', 'read.data'],
    environments: ['local-dev'],
    models: ['claude-haiku-4-5-20251001'],
    maxDailyBudgetUsd: 1,
  },
  'team-admin': {
    scopes: ['read.source', 'read.data', 'write.repo', 'write.data', 'call.api'],
    environments: ['local-dev', 'cloud-dev'],
    models: ['claude-haiku-4-5-20251001', 'claude-sonnet-5'],
    maxDailyBudgetUsd: 10,
  },
  'security-admin': {
    scopes: ['read.source', 'read.data', 'write.repo', 'write.data', 'call.api'],
    environments: ['local-dev', 'cloud-dev'],
    models: ['claude-haiku-4-5-20251001', 'claude-sonnet-5'],
    maxDailyBudgetUsd: 10,
  },
  owner: {
    scopes: ['read.source', 'read.data', 'write.repo', 'write.data', 'call.api', 'deploy'],
    environments: ['local-dev', 'cloud-dev', 'prod'],
    models: ['claude-haiku-4-5-20251001', 'claude-sonnet-5', 'claude-opus-5'],
    maxDailyBudgetUsd: 100,
  },
}

export function ceilingFor(role: TenantRole): AuthorityCeiling {
  return CEILINGS[role] ?? CEILINGS.member!
}

/** Instant local pre-check only -- returns a refusal reason if `value` for question `q` exceeds
 * `role`'s ceiling, else null. Only `scopes`, `environments`, `model` and `budget` are ceiling-checked
 * (matches §6b: `agent`/`role`/`never`/`escalate`/`effort`/`contracts` have no ceiling). */
export function overreach(q: IntakeQuestion, value: unknown, role: TenantRole): string | null {
  const c = ceilingFor(role)
  if (q.id === 'scopes' && Array.isArray(value)) {
    const bad = value.filter((v) => !c.scopes.includes(v))
    if (bad.length) return `${role} may not grant scope${bad.length > 1 ? 's' : ''} ${bad.join(', ')} -- ceiling is ${c.scopes.join(', ')}.`
  }
  if (q.id === 'environments' && Array.isArray(value)) {
    const bad = value.filter((v) => !c.environments.includes(v))
    if (bad.length) return `${role} may not birth an agent into ${bad.join(', ')} -- ceiling is ${c.environments.join(', ')}.`
  }
  if (q.id === 'model' && typeof value === 'string' && !c.models.includes(value)) {
    return `${role} may not assign model ${value} -- ceiling is ${c.models.join(', ')}.`
  }
  if (q.id === 'budget' && value && typeof value === 'object') {
    const usd = Number((value as { usd_per_day?: unknown }).usd_per_day)
    if (Number.isFinite(usd) && usd > c.maxDailyBudgetUsd) {
      return `${role} may not set a daily budget above $${c.maxDailyBudgetUsd} -- requested $${usd}.`
    }
  }
  return null
}

export type CeilingCheck =
  // Bob, 2026-09-23: a missing/`{}` budget answer defaults to the role ceiling rather than
  // refusing or compiling uncapped. `defaulted` is present only when a default was applied --
  // never present on a normal in-ceiling answer.
  | { allowed: true; defaulted?: { usd_per_day: number } }
  | { allowed: false; reason: string; details: IntakeCeilingDetails }
  | { allowed: null; error: string } // the check call itself failed (network, 401, ...) -- not a ceiling verdict

/** `POST /v1/mandates/intake/check` (§6b) -- the ceiling of record. Refuses at the question, with
 * a machine-readable reason, regardless of what `overreach` above already guessed. */
export async function checkIntakeAnswer(questionId: string, value: unknown, token: string | null): Promise<CeilingCheck> {
  const r = await api<{ allowed: true; defaulted?: { usd_per_day: number } }>('/mandates/intake/check', { method: 'POST', body: JSON.stringify({ question_id: questionId, value }) }, token)
  if (isOk(r)) return { allowed: true, ...(r.data.defaulted ? { defaulted: r.data.defaulted } : {}) }
  if (r.error.code === 'authority_ceiling') return { allowed: false, reason: r.error.message, details: r.error.details as IntakeCeilingDetails }
  return { allowed: null, error: r.error.message }
}
